import { ZegoUIKitPrebuilt } from '@zegocloud/zego-uikit-prebuilt';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { verifyVideoCallSessionApi } from '../lib/api/consultation.api';
import {
  useNotification,
  useSessionNotifications,
  useSessionTiming,
  useStartMeeting,
} from '../lib/hooks';
import { useEndCall } from '../lib/hooks/useEndCall';
import { generateMeetingToken } from '../lib/utils/zego-utils';
import { SessionParticipant, UserRole, VideoCallProps } from '../types/videocall.types';
import VideoCallStatus from './VideoCallStatus';

const VideoCall: React.FC<VideoCallProps> = ({ appConfig }) => {
  const { consultationId } = useParams<{ consultationId: string }>();
  const [searchParams] = useSearchParams();
  const meetingContainerRef = useRef<HTMLDivElement>(null);
  const [participant, setParticipant] = useState<SessionParticipant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [zegoInstance, setZegoInstance] = useState<ZegoUIKitPrebuilt | null>(null);
  const [meetingStarted, setMeetingStarted] = useState(false);
  const sessionTiming = useSessionTiming(participant);
  const { notification } = useNotification({
    isOpen: false,
    message: '',
    severity: 'warning',
  });

  const userId = searchParams.get('userId') || uuidv4();
  const userRole = (searchParams.get('role') || 'USER') as UserRole;

  const renderLoading = () => <VideoCallStatus status="loading" />;
  const renderError = () => <VideoCallStatus status="error" errorMessage={error} />;
  const renderRedirecting = () => <VideoCallStatus status="redirecting" />;

  const redirectToDashboard = useCallback(() => {
    const redirectPath =
      userRole === 'CONSULTANT'
        ? '/consultant/dashboard/sessions?status=action_required'
        : '/consultee/dashboard/sessions?status=action_required';
    window.location.href = `${appConfig.baseUrl}${redirectPath}`;
  }, [userRole, appConfig.baseUrl]);

  const initializeZegoCloud = useCallback(
    async (participant: SessionParticipant) => {
      try {
        const userName = participant.firstName || `User-${uuidv4().slice(0, 6)}`;

        if (!consultationId) {
          throw new Error('Missing consultation ID');
        }

        const kitToken = generateMeetingToken(
          appConfig.appId,
          appConfig.serverSecret,
          consultationId,
          userId,
          userName,
        );

        return await ZegoUIKitPrebuilt.create(kitToken);
      } catch (error) {
        console.error('Failed to initialize ZegoCloud:', error);
        throw error;
      }
    },
    [appConfig, consultationId, userId],
  );

  const startMeeting = useStartMeeting({
    meetingContainerRef,
    initializeZegoCloud,
    redirectToDashboard,
    setError,
    setLoading,
    setMeetingStarted,
    setZegoInstance,
    meetingStarted,
  });

  const endCall = useEndCall({
    zegoInstance,
    setZegoInstance,
    setRedirecting,
    redirectToDashboard,
    setError,
  });

  useEffect(() => {
    const fetchConsultationDetails = async () => {
      try {
        if (!consultationId) {
          throw new Error('Missing consultation ID');
        }
        
        // Demo mode: Skip API verification if consultationId starts with 'demo-'
        if (consultationId.startsWith('demo-')) {
          const demoParticipant: SessionParticipant = {
            role: userRole,
            sessionLength: 30,
            id: userId,
            firstName: userRole === 'CONSULTANT' ? 'Dr. Demo' : 'Demo User',
            time: new Date().toISOString(),
          };
          setParticipant(demoParticipant);
          setLoading(false);
          return;
        }

        // Demo mode: Skip API verification if consultationId starts with 'demo-'
        if (consultationId.startsWith('demo-')) {
          const demoParticipant: SessionParticipant = {
            role: userRole,
            sessionLength: 30,
            id: userId,
            firstName: userRole === 'CONSULTANT' ? 'Dr. Demo' : 'Demo User',
            time: new Date().toISOString(),
          };
          setParticipant(demoParticipant);
          setLoading(false);
          return;
        }

        const data = await verifyVideoCallSessionApi(userId, consultationId, userRole);
        if (!data) {
          throw new Error('Invalid API response');
        }

        setParticipant(data);
        setLoading(false);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('API Error:', errorMessage);
        setError(`Failed to fetch meeting details: ${errorMessage}`);
        setLoading(false);
      }
    };

    fetchConsultationDetails();
  }, [consultationId, userId, userRole]);

  useEffect(() => {
    if (participant && !meetingStarted && !loading && !error) {
      console.log('Starting meeting with participant:', participant);
      startMeeting(participant).catch(err => {
        console.error('Failed to start meeting:', err);
        setError(`Failed to start meeting: ${err.message}`);
      });
    }
  }, [participant, meetingStarted, loading, error, startMeeting]);

  useSessionNotifications({ sessionTiming, endCall });

  // Enforce mirror effect for local video
  useEffect(() => {
    const applyMirrorEffect = () => {
      const videos = document.querySelectorAll('video');
      videos.forEach((video) => {
        const htmlVideo = video as HTMLVideoElement;
        // Apply mirror effect (flip horizontally)
        htmlVideo.style.transform = 'scaleX(-1)';
        htmlVideo.style.webkitTransform = 'scaleX(-1)';
      });
    };

    // Apply immediately and repeatedly
    applyMirrorEffect();
    const intervalId = setInterval(applyMirrorEffect, 500);

    // Observer to catch new video elements
    const observer = new MutationObserver(applyMirrorEffect);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearInterval(intervalId);
      observer.disconnect();
    };
  }, []);

  if (loading || error || redirecting) {
    if (loading) return renderLoading();
    if (error) return renderError();
    if (redirecting) return renderRedirecting();
  }

  return (
    <div className="relative w-full h-screen">
      <div ref={meetingContainerRef} className="w-full h-screen" />

      {notification.isOpen && (
        <div className="fixed top-4 left-4 bg-yellow-500 text-white p-4 rounded-lg flex items-center gap-2 shadow-lg">
          <span>⏳</span>
          <p>{notification.message}</p>
        </div>
      )}
    </div>
  );
};

export default VideoCall;
